/**
 * Helper utility for tracking LiveRamp operations in New Relic.
 * Provides a centralized method for sending page actions to New Relic.
 */

// Augment Window interface to include NewRelic browser agent
declare global {
    interface Window {
        newrelic?: {
            addPageAction: (
                name: string,
                attributes?: Record<string, any>,
            ) => void;
        };
    }
}

/**
 * Tracks a LiveRamp-related action in New Relic.
 * Safely handles cases where New Relic is not available.
 * Only tracks if the 'f_liveramp_debug' feature flag is enabled.
 *
 * @param actionName - The name of the action to track (e.g., 'LiveRamp_API_Started')
 * @param attributes - Optional attributes/metadata to include with the action
 */
export const trackLiveRampAction = (
    actionName: string,
    attributes?: Record<string, any>,
): void => {
    try {
        // Check if New Relic is available
        if (typeof window === 'undefined' || !window.newrelic) {
            return;
        }

        window.newrelic.addPageAction(actionName, attributes);
    } catch (error) {
        // Silently fail if New Relic is not available or encounters an error
        // We don't want tracking issues to break LiveRamp functionality
        console.debug('[LiveRamp] New Relic tracking failed:', error);
    }
};
